import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSquare, Copy, Check, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PartnerPitchKitPage() {
  const { partner } = useOutletContext();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('rpace_token');
    fetch(`${API_URL}/api/partners/pitch-templates`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => setTemplates(data))
      .catch(() => toast.error('Failed to load templates'))
      .finally(() => setLoading(false));
  }, []);

  const fillTemplate = (template) => {
    return template
      .replace(/{partner_name}/g, partner?.name || 'Your Name')
      .replace(/{firm_name}/g, partner?.firm_name || 'Your Firm')
      .replace(/{director_name}/g, '[Director Name]')
      .replace(/{company_name}/g, '[Company Name]')
      .replace(/{cin}/g, '[CIN]')
      .replace(/{estimated_savings}/g, '[Amount]')
      .replace(/{pending_fys}/g, '[X]')
      .replace(/{intent_label}/g, 'Strike-off / Regularise');
  };

  const handleCopy = async (id, template) => {
    const text = fillTemplate(template);
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success('Copied! Paste in WhatsApp');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedId(id);
      toast.success('Copied! Paste in WhatsApp');
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8" data-testid="pitch-kit-page">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-[#1e3a5f] flex items-center gap-2">
          <MessageSquare className="w-5 h-5" /> Pitch Kit
        </h1>
        <p className="text-xs text-slate-500 mt-1">Ready-to-send WhatsApp messages for client outreach. Tap to copy, paste in WhatsApp.</p>
      </div>

      <div className="space-y-3">
        {templates.map(t => {
          const isExpanded = expandedId === t.id;
          const isCopied = copiedId === t.id;
          const filledText = fillTemplate(t.template);

          return (
            <Card key={t.id} className="overflow-hidden" data-testid={`pitch-template-${t.id}`}>
              <button
                onClick={() => setExpandedId(isExpanded ? null : t.id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center shrink-0">
                    <MessageSquare className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{t.title}</p>
                    <p className="text-[10px] text-slate-400">{t.scenario}</p>
                  </div>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                  {/* Preview */}
                  <div className="bg-green-50/50 border border-green-200 rounded-lg p-4 mb-3">
                    <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed" data-testid={`pitch-preview-${t.id}`}>
                      {filledText}
                    </p>
                  </div>
                  <p className="text-[10px] text-slate-400 mb-3">
                    Replace [Director Name], [Company Name], [Amount] etc. with actual details before sending.
                  </p>
                  <Button
                    onClick={() => handleCopy(t.id, t.template)}
                    className={`w-full gap-2 text-sm ${isCopied ? 'bg-green-600 hover:bg-green-700' : 'bg-[#1e3a5f] hover:bg-[#152d4a]'}`}
                    data-testid={`copy-btn-${t.id}`}
                  >
                    {isCopied ? <><Check className="w-4 h-4" /> Copied! Paste in WhatsApp</> : <><Copy className="w-4 h-4" /> Copy Message</>}
                  </Button>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
